"use client";

import * as React from "react";
import { CheckCircle2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { InfoTooltip } from "./info-tooltip";

export interface PayoutDetailsCardProps {
  grossAmount?: string;
  platformFeeAmount?: string;
  platformFeeDisplayMode?: "default" | "deduction";
  pendingAmount: string;
  dueAmount?: string;
  nextPayout?: string;
  payoutMethod: string;
  title?: string;
  grossLabel?: string;
  platformFeeLabel?: string;
  pendingLabel?: string;
  dueLabel?: string;
  nextPayoutLabel?: string;
  payoutMethodLabel?: string;
  payoutScheduleLabel?: string;
  readMoreLabel?: string;
  grossTooltip?: string;
  platformFeeTooltip?: string;
  pendingTooltip?: string;
  dueTooltip?: string;
  className?: string;
  onPayoutMethodClick?: () => void;
  onReadMoreClick?: () => void;
}

export function PayoutDetailsCard({
  grossAmount,
  platformFeeAmount,
  platformFeeDisplayMode = "default",
  pendingAmount,
  dueAmount,
  nextPayout,
  payoutMethod,
  title = "Payout Details",
  grossLabel = "Gross Earnings",
  platformFeeLabel = "Platform Fees",
  pendingLabel = "Pending",
  dueLabel = "Due",
  nextPayoutLabel = "Next Payout",
  payoutMethodLabel = "Payout Method",
  payoutScheduleLabel = "Current month's earning will be paid on the 15th of next month.",
  readMoreLabel = "Read more",
  grossTooltip = "Total earnings generated, including both finalized and in-progress amounts, before platform fees.",
  platformFeeTooltip = "This small fees help us pay the bills so that we can keep artha.link running",
  pendingTooltip = "Scheduled for the next payout cycle",
  dueTooltip = "Scheduled for the current payout cycle",
  className,
  onPayoutMethodClick,
  onReadMoreClick,
}: PayoutDetailsCardProps) {
  const renderAmountRow = (
    label: string,
    amount: string | undefined,
    tooltip: string | undefined,
    variant: "default" | "deduction" = "default"
  ) => {
    if (!amount) return null;

    const displayAmount = variant === "deduction" ? `- ${amount}` : amount;

    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium text-[#667085]">{label}</p>
          {tooltip ? (
            <InfoTooltip
              content={tooltip}
              align="left"
              className="ml-0.5"
            />
          ) : null}
        </div>
        <p
          className={cn(
            "whitespace-nowrap text-[#344054]",
            variant === "deduction" ? "text-sm font-medium" : "text-xl font-semibold"
          )}
        >
          {displayAmount}
        </p>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-black bg-white p-3 shadow-[0_4px_0_0_#000000]",
        className
      )}
    >
      <p className="text-xs font-medium text-[#98A2B3]">{title}</p>
      <div className="flex flex-col gap-3">
        {renderAmountRow(grossLabel, grossAmount, grossTooltip)}
        {renderAmountRow(
          platformFeeLabel,
          platformFeeAmount,
          platformFeeTooltip,
          platformFeeDisplayMode
        )}
        {renderAmountRow(pendingLabel, pendingAmount, pendingTooltip)}
        {renderAmountRow(dueLabel, dueAmount, dueTooltip)}

        {nextPayout && (
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[#667085]">{nextPayoutLabel}</p>
            <p className="whitespace-nowrap text-sm font-medium text-[#344054]">{nextPayout}</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-[#667085]">{payoutMethodLabel}</p>
          <button
            type="button"
            onClick={onPayoutMethodClick}
            className="inline-flex items-center gap-1 rounded bg-[rgba(46,182,125,0.12)] px-2 py-1 hover:bg-[rgba(46,182,125,0.2)] transition-colors cursor-pointer"
          >
            <span className="text-sm font-medium text-[#475467]">{payoutMethod}</span>
            <CheckCircle2 className="size-4 text-[#2EB67D]" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-1 border-t border-[#F2F4F7] pt-2">
          <span className="text-[10px] leading-tight text-[#667085]">{payoutScheduleLabel}</span>
          <button
            onClick={onReadMoreClick}
            className="text-[10px] font-medium text-[#0D41E1] underline hover:text-[#0a35b8] cursor-pointer"
          >
            {readMoreLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
